import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useDeleteSessionMutation, useUpdateSessionMetadataMutation } from '../../../../../store/media/media'
import { setRoomSessionDeleteLoading, setRoomSessionMetadataUpdateLoading, updateRoomSessionMetadata } from '../../../../../store/meetingSummary/roomSession'
import { useCallback } from 'react'

import { useExecuteSessionMutation, useExecuteSystemPresetForSessionMutation } from '../../../../../store/ai/aiExecute'
import {
  addAiResult,
  setAiResult,
  setAiResultRerunLoading,
  setAiResultSaveLoading,
  setAiResultScoreLoading,
  setSavedResultId,
} from '../../../../../store/meetingSummary/aiResults'
import { useOverrideAiResultMutation, useSaveAiResultMutation, useScoreAiResultMutation } from '../../../../../store/ai/ai'
import { AiResultCreate, AiResultOverride } from '../../../../../models/AiResult'
import {
  CreateCommentPayload,
  UpdateCommentPayload,
  useCreateCommentMutation,
  useCreateMeetingNoteMutation,
  useUpdateCommentMutation,
  useUpdateMeetingNoteMutation,
} from '../../../../../store/meetingSupport/meetingSupport'
import {
  addComment,
  selectMeetingNoteId,
  setComment,
  setCommentUpdateLoading,
  setMeetingNote,
  setMeetingNoteId,
  setMeetingNoteUpdateLoading,
  setResultExplainerLoading,
  setSavedComment,
} from '../../../../../store/meetingSummary/meetingSupport'
import { RESULT_EXPLAINER_PRESET_NAME } from '../ResultExplainer/ResultExplainerPresetMapping'
import { ExplainedResult } from '../ResultExplainer/models'
import { nanoid } from 'nanoid'

export const useUpdateRoomSessionMetadata = () => {
  const dispatch = useAppDispatch()

  const { roomId = '', sessionId = '' } = useParams()

  const accountId = useAppSelector(selectAccountId)

  const [updateMetadata] = useUpdateSessionMetadataMutation()

  return useCallback(
    async (newMetadata: { [key: string]: any }) => {
      dispatch(setRoomSessionMetadataUpdateLoading(true))
      try {
        const { data: roomSession } = await updateMetadata({ accountId, roomId, sessionId, metadata: newMetadata }).unwrap()
        dispatch(updateRoomSessionMetadata(roomSession.metadata))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setRoomSessionMetadataUpdateLoading(false))
      }
    },
    [dispatch, updateMetadata, accountId, roomId, sessionId],
  )
}

export const useDeleteRoomSession = () => {
  const dispatch = useAppDispatch()

  const { roomId = '', sessionId = '' } = useParams()
  const accountId = useAppSelector(selectAccountId)

  const [deleteSession] = useDeleteSessionMutation()

  return useCallback(async () => {
    dispatch(setRoomSessionDeleteLoading(true))
    try {
      await deleteSession({ accountId, roomId, sessionId })
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setRoomSessionDeleteLoading(false))
    }
  }, [dispatch, deleteSession, accountId, roomId, sessionId])
}

export const useExecuteAiFeatures = () => {
  const { roomId = '', sessionId = '' } = useParams()
  const accountId = useAppSelector(selectAccountId)

  const [executeAiFeatureOnSession] = useExecuteSessionMutation()

  return useCallback(
    (aiFeatureIds: string[], meetingTemplateId: string, context?: { [key: string]: any }) => {
      return executeAiFeatureOnSession({
        accountId,
        roomId,
        sessionId,
        payload: { aiFeatureIds: aiFeatureIds, saveResults: true, meetingTemplateId, context },
      })
    },
    [accountId, roomId, sessionId, executeAiFeatureOnSession],
  )
}

export const useRerunAiFeature = () => {
  const dispatch = useAppDispatch()

  const { roomId = '', sessionId = '' } = useParams()
  const accountId = useAppSelector(selectAccountId)

  const [executeAiFeatureOnSession] = useExecuteSessionMutation()

  return useCallback(
    async (aiFeatureId: string, meetingTemplateId: string) => {
      const rerunId = nanoid()
      dispatch(setAiResultRerunLoading({ isLoading: true, rerunId }))
      try {
        return await executeAiFeatureOnSession({ accountId, roomId, sessionId, payload: { aiFeatureIds: [aiFeatureId], meetingTemplateId } }).unwrap()
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setAiResultRerunLoading({ isLoading: false, rerunId }))
      }
    },
    [dispatch, accountId, roomId, sessionId, executeAiFeatureOnSession],
  )
}

export const useSaveAiResult = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [saveAiResult] = useSaveAiResultMutation()

  return useCallback(
    async (aiResultCreate: AiResultCreate) => {
      dispatch(setAiResultSaveLoading(true))
      try {
        const aiResult = await saveAiResult({ accountId, payload: aiResultCreate }).unwrap()
        dispatch(addAiResult(aiResult))
        dispatch(setSavedResultId(aiResult.id))
        return aiResult
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setAiResultSaveLoading(false))
      }
    },
    [dispatch, saveAiResult, accountId],
  )
}

const removeEmptyStrings = (value: any): any => {
  if (Array.isArray(value)) {
    return value.filter((item) => item !== '').map(removeEmptyStrings)
  } else if (typeof value === 'object' && value !== null) {
    const cleanedObject: { [key: string]: any } = {}
    Object.entries(value).forEach(([key, val]) => {
      if (val !== '') {
        cleanedObject[key] = removeEmptyStrings(val)
      }
    })
    return cleanedObject
  }
  return value === '' ? null : value
}

export const useOverrideAiResult = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [overrideAiResult] = useOverrideAiResultMutation()

  return useCallback(
    async (aiResultId: string, aiResultOverride: AiResultOverride, afterOverride?: () => Promise<void>) => {
      dispatch(setAiResultSaveLoading(true))

      try {
        const cleanedResult = { ...aiResultOverride, result: removeEmptyStrings(aiResultOverride.result) }
        const overriddenAiResult = await overrideAiResult({ accountId, aiResultId, payload: cleanedResult }).unwrap()
        dispatch(setAiResult(overriddenAiResult))
        dispatch(setSavedResultId(overriddenAiResult.id))
        if (afterOverride) {
          await afterOverride()
        }
        return overriddenAiResult
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setAiResultSaveLoading(false))
      }
    },
    [dispatch, overrideAiResult, accountId],
  )
}

export const useScoreAiResult = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [scoreAiResult] = useScoreAiResultMutation()

  return useCallback(
    async (aiResultId: string, score: number) => {
      dispatch(setAiResultScoreLoading(true))
      try {
        const scoredAiResult = await scoreAiResult({ accountId, aiResultId, payload: { score } }).unwrap()
        dispatch(setAiResult(scoredAiResult))
        return scoredAiResult
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setAiResultScoreLoading(false))
      }
    },
    [dispatch, scoreAiResult, accountId],
  )
}

export const useUpdateMeetingNote = () => {
  const dispatch = useAppDispatch()

  const { roomId = '', sessionId = '' } = useParams()

  const accountId = useAppSelector(selectAccountId)
  const meetingNoteId = useAppSelector(selectMeetingNoteId)

  const [_createMeetingNote] = useCreateMeetingNoteMutation()
  const [_updateMeetingNote] = useUpdateMeetingNoteMutation()

  return useCallback(
    async (meetingNote: string) => {
      dispatch(setMeetingNoteUpdateLoading(true))
      try {
        if (!meetingNoteId) {
          const {
            data: { id: createdMeetingNoteId },
          } = await _createMeetingNote({ accountId, roomId, sessionId, payload: { content: meetingNote, format: 'rich_text' } }).unwrap()
          dispatch(setMeetingNoteId(createdMeetingNoteId))
        } else {
          await _updateMeetingNote({ accountId, roomId, sessionId, meetingNoteId, payload: { content: meetingNote, format: 'rich_text' } })
        }
        dispatch(setMeetingNote(meetingNote))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setMeetingNoteUpdateLoading(false))
      }
    },
    [dispatch, _createMeetingNote, _updateMeetingNote, accountId, roomId, sessionId, meetingNoteId],
  )
}

export const useCreateComment = () => {
  const dispatch = useAppDispatch()

  const { roomId = '', sessionId = '' } = useParams()

  const accountId = useAppSelector(selectAccountId)

  const [createComment] = useCreateCommentMutation()

  return useCallback(
    async (commentCreate: CreateCommentPayload) => {
      dispatch(setCommentUpdateLoading(true))
      try {
        const comment = await createComment({ accountId, roomId, sessionId, payload: commentCreate }).unwrap()
        dispatch(addComment(comment))
        dispatch(setSavedComment(comment))
        return comment
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setCommentUpdateLoading(false))
      }
    },
    [dispatch, createComment, accountId, roomId, sessionId],
  )
}

export const useUpdateComment = () => {
  const dispatch = useAppDispatch()

  const { roomId = '', sessionId = '' } = useParams()

  const accountId = useAppSelector(selectAccountId)

  const [updateComment] = useUpdateCommentMutation()

  return useCallback(
    async (commentId: string, commentUpdate: UpdateCommentPayload) => {
      dispatch(setCommentUpdateLoading(true))
      try {
        const updatedComment = await updateComment({ accountId, roomId, sessionId, commentId, payload: commentUpdate }).unwrap()
        dispatch(setComment(updatedComment))
        return updatedComment
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setCommentUpdateLoading(false))
      }
    },
    [dispatch, updateComment, accountId, roomId, sessionId],
  )
}

export const useExplainResult = () => {
  const dispatch = useAppDispatch()

  const { roomId = '', sessionId = '' } = useParams()

  const accountId = useAppSelector(selectAccountId)

  const resultExplainerPresetName = RESULT_EXPLAINER_PRESET_NAME

  const [executeSystemPresetForSession] = useExecuteSystemPresetForSessionMutation()

  return useCallback(
    async (resultToExplain: string, aiFeaturePresetDescription: string) => {
      dispatch(setResultExplainerLoading(true))
      try {
        const explanationResults = await executeSystemPresetForSession({
          accountId,
          roomId,
          sessionId,
          payload: {
            systemPresetName: resultExplainerPresetName,
            context: {
              result_to_explain: resultToExplain,
              preset_description: aiFeaturePresetDescription,
            },
          },
        }).unwrap()
        if (!explanationResults.length || !explanationResults[0].result || explanationResults[0].result.type !== 'structuredSummary') {
          return null
        }
        return explanationResults[0].result.result as unknown as ExplainedResult
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setResultExplainerLoading(false))
      }
      return null
    },
    [dispatch, executeSystemPresetForSession, accountId, roomId, sessionId, resultExplainerPresetName],
  )
}
